import { Fragment, useEffect, useMemo, useState } from 'react'
import { RoLockedView } from '../../../components/billing/RoLocked'
import { useHistory, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  Car,
  Download,
  CheckCircle2,
  Copy,
  Loader2,
  AlertCircle,
  ChevronDown,
  Plus,
  Pencil,
  Scan,
  AlertTriangle,
  CloudHail
} from 'lucide-react'
import { EstimateLockIcon } from '../EstimateLockIcon'
import { Button } from '@components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select'
import { FindingCard, AddCalibrationForm } from '@components/adas-intelligence'
import { BrandedModal } from '@components/branded-modal'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@components/ui/dialog'
import { useAdasIntelligenceStore } from '@caradasstore/AdasIntelligenceStore'
import { useCenterStore } from '@caradasstore/CenterStore'
import { useMobileToolbarStore } from '@caradasstore/MobileToolbarStore'
import { useUserStore } from '@caradasstore/UserStore'
import { useClientAndInsuranceData } from '@hooks/useClientAndInsuranceData'
import { useIsReadOnly } from '@hooks/useIsReadOnly'
import { useModuleAccess } from '@hooks/useModuleAccess'
import {
  buildAdasReportFilename,
  buildEstimateFilename,
  downloadOriginalEstimateFile,
  downloadScrubEstimatePdf,
  getScrubEstimateHistory
} from '../../../api/scrubApi'
import { REDIRECT_URLS } from '../../../constants'
import { describeCollisionAssessment } from '../adapters'
import { fuzzyMatchInsurers, matchWithFuzzy } from '../../../utils/fuzzyMatchClients'

/** Tone + icon per CollisionClassification severity. HAIL is not a collision, so it reads
 *  as neutral information (blue, cloud icon) rather than a damage warning. */
const COLLISION_BADGE_TONES = {
  high: { tone: 'border-[#FCA5A5] bg-[#FEF2F2] text-[#B91C1C]', Icon: AlertTriangle },
  medium: { tone: 'border-[#FCD34D] bg-[#FFFBEB] text-[#B45309]', Icon: AlertTriangle },
  info: { tone: 'border-[#BFDBFE] bg-[#EFF6FF] text-[#1D4ED8]', Icon: CloudHail }
}

/** Collision verdict badge for the AI Report header (CAR-3804). Renders for COLLISION,
 *  LIKELY_COLLISION and HAIL — a NON_COLLISION, unknown, or absent `collisionAssessment`
 *  leaves the header exactly as it was. The hover text spells out the score and the
 *  signals that tripped so the verdict is explainable, not opaque. */
/** CAR-3876: the BE creates the quote for an auto-quote client a few seconds to ~30s after the
 *  upload completes. While that can still happen, poll the estimate instead of offering
 *  Create Quote (which would race it into a duplicate). After the window, fall back. */
const AUTO_QUOTE_WAIT_MS = 2 * 60 * 1000
const AUTO_QUOTE_POLL_MS = 3000

const CollisionBadge = ({ collision, className = '' }) => {
  const style = collision?.showBadge ? COLLISION_BADGE_TONES[collision.severity] : null
  if (!style) return null
  const { tone, Icon } = style
  return (
    <span
      title={describeCollisionAssessment(collision)}
      className={`inline-flex items-center gap-1 rounded-badge border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${tone} ${className}`}
    >
      <Icon className='h-3 w-3 flex-none' aria-hidden='true' />
      {collision.label}
    </span>
  )
}

export const EstimateDetailPage = () => {
  const { id } = useParams()
  const history = useHistory()
  const estimate = useAdasIntelligenceStore((s) => s.detailsById[id])
  const isDetailLoading = useAdasIntelligenceStore((s) => s.isDetailLoading)
  const detailError = useAdasIntelligenceStore((s) => s.detailError)
  const detailRefusal = useAdasIntelligenceStore((s) => s.detailRefusal)
  const loadDetail = useAdasIntelligenceStore((s) => s.loadDetail)
  const markReviewed = useAdasIntelligenceStore((s) => s.markReviewed)
  const addCalibration = useAdasIntelligenceStore((s) => s.addCalibration)
  const removeCalibration = useAdasIntelligenceStore((s) => s.removeCalibration)
  const restoreCalibration = useAdasIntelligenceStore((s) => s.restoreCalibration)
  const patchEstimateMeta = useAdasIntelligenceStore((s) => s.patchEstimateMeta)
  const currentUser = useUserStore((store) => store.currentUser)

  const ccId = useCenterStore((store) => store.ccId)
  const { clients, insuranceCompanies } = useClientAndInsuranceData(ccId)

  const [isDownloading, setIsDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState(null)
  const [copied, setCopied] = useState(false)
  const [isMarking, setIsMarking] = useState(null) // 'review' | 'quote' | null
  const [versionHistory, setVersionHistory] = useState([])
  const [isPreparingQuote, setIsPreparingQuote] = useState(false)
  const [isAwaitingAutoQuote, setIsAwaitingAutoQuote] = useState(false)
  // Creating a quote from an estimate writes; reading the estimate does not.
  const isReadOnly = useIsReadOnly()
  // ...and lands in Quotes, which the AI Report plan does not include (CAR-3710).
  const canUse = useModuleAccess()
  const canCreateQuote = !isReadOnly && canUse('quotes')
  // Map keyed by system name (stable across backend versions) → { system, section, category, calType, dismissedAt }
  // Persisted per-RO in localStorage so a refresh keeps showing the same dismissed cards.
  const [dismissedBySystem, setDismissedBySystem] = useState(() => new Map())
  // Set of system names the user restored locally; suppresses BE history that hasn't caught
  // up yet (estimate.removedCalibrations still contains the old delete record). Persisted
  // per-RO and pruned automatically once the BE active findings include the system.
  const [restoredBySystem, setRestoredBySystem] = useState(() => new Set())
  // Set of system names that were added manually via the ADD API — drives the "Custom" pill.
  const [customSystems, setCustomSystems] = useState(() => new Set())
  // Optimistic cards rendered immediately after the user clicks Add (before the backend round-trip completes).
  const [pendingAdditions, setPendingAdditions] = useState([])
  const [showDismissed, setShowDismissed] = useState(true)
  // The finding awaiting a dismiss confirmation. Clicking the X on a card no longer
  // removes it immediately — it stages { finding, section, isCustom } here and opens a
  // confirm dialog; the actual removal runs from performDismiss only on confirm.
  const [pendingDismiss, setPendingDismiss] = useState(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [isAdding, setIsAdding] = useState(false)
  const [calibrationError, setCalibrationError] = useState(null)
  const [showAllHistory, setShowAllHistory] = useState(false)
  const [selectedInsurance, setSelectedInsurance] = useState('')
  const [insurancePickerOpen, setInsurancePickerOpen] = useState(false)
  const [insurancePickerSearch, setInsurancePickerSearch] = useState('')
  const [selectedClient, setSelectedClient] = useState('')
  const [clientPickerOpen, setClientPickerOpen] = useState(false)
  const [clientPickerSearch, setClientPickerSearch] = useState('')

  // Hydrate dismissed + restored maps from localStorage when the user lands on (or
  // navigates to) an RO. Persisting these means a page refresh keeps the dismissed
  // drawer state and any pending Restore actions intact.
  useEffect(() => {
    if (!estimate?.ro) return
    try {
      const dismissedRaw = localStorage.getItem(`adas-dismissed-${estimate.ro}`)
      const dismissedArr = dismissedRaw ? JSON.parse(dismissedRaw) : []
      setDismissedBySystem(Array.isArray(dismissedArr) ? new Map(dismissedArr) : new Map())
    } catch {
      setDismissedBySystem(new Map())
    }
    try {
      const restoredRaw = localStorage.getItem(`adas-restored-${estimate.ro}`)
      const restoredArr = restoredRaw ? JSON.parse(restoredRaw) : []
      setRestoredBySystem(Array.isArray(restoredArr) ? new Set(restoredArr) : new Set())
    } catch {
      setRestoredBySystem(new Set())
    }
    setPendingAdditions([])
  }, [estimate?.ro])

  // Persist dismissedBySystem on each change so a refresh keeps the same dismissed cards.
  useEffect(() => {
    if (!estimate?.ro) return
    try {
      localStorage.setItem(`adas-dismissed-${estimate.ro}`, JSON.stringify(Array.from(dismissedBySystem.entries())))
    } catch {
      /* ignore storage quota */
    }
  }, [estimate?.ro, dismissedBySystem])

  // Persist restoredBySystem so a Restore made just before refresh still hides the
  // BE-history record on reload until the active findings catch up.
  useEffect(() => {
    if (!estimate?.ro) return
    try {
      localStorage.setItem(`adas-restored-${estimate.ro}`, JSON.stringify(Array.from(restoredBySystem)))
    } catch {
      /* ignore storage quota */
    }
  }, [estimate?.ro, restoredBySystem])

  // Once the BE active findings catch up to a locally-restored system, drop it from
  // restoredBySystem — it's no longer needed to suppress the dismissed-drawer entry.
  useEffect(() => {
    if (!estimate?.findings || restoredBySystem.size === 0) return
    const activeSet = new Set()
    estimate.findings.forEach((g) => g.items.forEach((it) => it.system && activeSet.add(it.system)))
    let changed = false
    const next = new Set(restoredBySystem)
    restoredBySystem.forEach((s) => {
      if (activeSet.has(s)) {
        next.delete(s)
        changed = true
      }
    })
    if (changed) setRestoredBySystem(next)
  }, [estimate?.findings, restoredBySystem])

  // Once the backend detail includes a pending addition (by system name), drop the
  // optimistic stub so we don't double-render the card.
  useEffect(() => {
    if (!estimate?.findings?.length || pendingAdditions.length === 0) return
    const present = new Set()
    estimate.findings.forEach((g) => g.items.forEach((it) => it.system && present.add(it.system)))
    const remaining = pendingAdditions.filter((p) => !present.has(p.system))
    if (remaining.length !== pendingAdditions.length) setPendingAdditions(remaining)
  }, [estimate?.findings, pendingAdditions])

  // Hydrate manually-added ("custom") calibration system names from localStorage.
  useEffect(() => {
    if (!estimate?.ro) return
    try {
      const raw = localStorage.getItem(`adas-custom-${estimate.ro}`)
      if (!raw) return
      const arr = JSON.parse(raw)
      if (!Array.isArray(arr)) return
      setCustomSystems(new Set(arr.filter(Boolean)))
    } catch {
      /* ignore corrupt storage */
    }
  }, [estimate?.ro])

  // Persist custom-system list whenever it changes.
  useEffect(() => {
    if (!estimate?.ro) return
    try {
      localStorage.setItem(`adas-custom-${estimate.ro}`, JSON.stringify(Array.from(customSystems)))
    } catch {
      /* ignore storage quota */
    }
  }, [estimate?.ro, customSystems])

  // Sync selected insurance with the estimate's current value + available companies. Prefer the
  // backend-resolved canonical id (CAR-3767) so "STATE FARM INSURANCE" reflects as "State Farm";
  // fall back to matching the raw extracted string for older payloads without a resolved id. This
  // only sets the displayed selection — it does not persist (patch happens on explicit user change).
  useEffect(() => {
    if (!insuranceCompanies?.length) return
    if (estimate?.insuranceCompanyId != null) {
      const byId = insuranceCompanies.find((c) => c.id === estimate.insuranceCompanyId)
      if (byId) {
        setSelectedInsurance(String(byId.id))
        return
      }
    }
    if (!estimate?.insurance) return
    // Exact name, then the same fuzzy match the queue's bulk quote uses (filler words stripped,
    // confident hits only), so "Northstar Mutual" still lands on "North Star Mutual".
    const match = matchWithFuzzy(insuranceCompanies, estimate.insurance, fuzzyMatchInsurers)
    if (match) setSelectedInsurance(String(match.id))
  }, [estimate?.insurance, estimate?.insuranceCompanyId, insuranceCompanies])

  // Sync selected client with the estimate's current value + available clients (fetched
  // for the active calibration center). Keeps the dropdown highlighted on the current client
  // in all cases. When the estimate has no client, or the extracted client isn't in the list
  // (no-match), leave the selection empty so the trigger falls back to its placeholder (which
  // surfaces the extracted name when there is one).
  useEffect(() => {
    if (!clients?.length) return
    if (!estimate?.client || estimate.client === '—') {
      setSelectedClient('')
      return
    }
    const normalized = String(estimate.client).trim().toLowerCase()
    const match = clients.find((c) => (c.name || c.value || '').trim().toLowerCase() === normalized)
    setSelectedClient(match ? String(match.id) : '')
  }, [estimate?.client, clients])

  useEffect(() => {
    if (id) loadDetail(id)
  }, [id, loadDetail])

  // CAR-3876: a freshly uploaded estimate for a client with auto-quote on gets its quote created
  // by the BE shortly after the upload. Re-fetch the estimate until the linked quote shows up
  // (or the window closes), so the header flips to "Linked Quote" without a manual refresh.
  const clientAutoQuotes = useMemo(
    () => !!clients?.find((c) => String(c.id) === String(estimate?.clientId))?.auto_quote_enabled,
    [clients, estimate?.clientId]
  )
  const hasLinkedRecord = !!estimate?.record?.href
  // The BE now says whether the queue still owes a quote (auto_quote_job PENDING/RUNNING); the
  // time window below is only the fallback for a BE that predates that field.
  const queueOwesQuote = !!estimate?.autoQuote?.inProgress
  useEffect(() => {
    const createdMs = estimate?.createdAtIso ? Date.parse(estimate.createdAtIso) : NaN
    const remainingMs = createdMs + AUTO_QUOTE_WAIT_MS - Date.now()
    const shouldWait = !!id && !hasLinkedRecord && (queueOwesQuote || (clientAutoQuotes && remainingMs > 0))
    setIsAwaitingAutoQuote(shouldWait)
    if (!shouldWait) return undefined
    const poll = setInterval(() => loadDetail(id), AUTO_QUOTE_POLL_MS)
    // Known in-progress: keep polling until the state changes (bounded, in case the job is stuck).
    const windowMs = queueOwesQuote ? Math.max(remainingMs, 5 * 60 * 1000) : remainingMs
    const stop = setTimeout(() => {
      clearInterval(poll)
      setIsAwaitingAutoQuote(false)
    }, windowMs)
    return () => {
      clearInterval(poll)
      clearTimeout(stop)
    }
  }, [id, clientAutoQuotes, hasLinkedRecord, queueOwesQuote, estimate?.createdAtIso, loadDetail])

  // Fetch version history once we know the RO number
  useEffect(() => {
    if (!estimate?.ro) return
    let cancelled = false
    getScrubEstimateHistory(estimate.ro, ccId)
      .then((data) => {
        if (cancelled || !Array.isArray(data)) return
        setVersionHistory(
          data
            .sort((a, b) => (b.version ?? 0) - (a.version ?? 0))
            .map((h) => ({
              version: h.version,
              date: h.createdAt
                ? new Date(h.createdAt * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                : '',
              latest: !!h.latest
            }))
        )
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [estimate?.ro])

  const linesBySection = useMemo(() => {
    if (!estimate) return {}
    return estimate.estimateLines.reduce((acc, l) => {
      acc[l.section] = acc[l.section] || []
      acc[l.section].push(l)
      return acc
    }, {})
  }, [estimate])

  // Metadata rows for the Estimate Data Report header. Built from the same estimate object
  // rendered elsewhere; falls back to an em-dash so a missing value never blanks a row.
  const reportMeta = useMemo(() => {
    if (!estimate) return []
    const dash = (v) => (v == null || v === '' ? '—' : v)
    const receivedFull = estimate.dateReceivedIso
      ? new Date(estimate.dateReceivedIso).toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        })
      : ''
    const sourceDoc = [
      `Preliminary Supplement ${estimate.version ?? 1} with Summary`,
      estimate.version != null ? `Version ${estimate.version}` : null,
      receivedFull ? `received ${receivedFull}` : null
    ]
      .filter(Boolean)
      .join(' · ')
    return [
      { label: 'RO #', value: dash(estimate.ro) },
      { label: 'Vehicle', value: dash(estimate.vehicle) },
      { label: 'VIN', value: dash(estimate.vin) },
      { label: 'Insurance Company', value: dash(estimate.insurance) },
      { label: 'Company', value: dash(estimate.client) },
      { label: 'Source Document', value: sourceDoc }
    ]
  }, [estimate])

  // Mobile global header: register a Download icon in place of search/filter.
  // Hooks must run on every render — this useEffect intentionally lives above the
  // loading / not-found early returns to keep hook order stable.
  const setPageAction = useMobileToolbarStore((s) => s.setPageAction)
  const clearPageAction = useMobileToolbarStore((s) => s.clearPageAction)
  useEffect(() => {
    if (!estimate?.id) return undefined
    setPageAction({
      label: 'Download PDF',
      icon: Download,
      disabled: isDownloading,
      onClick: () => {
        if (!estimate?.id || isDownloading) return
        setIsDownloading(true)
        setDownloadError(null)
        downloadScrubEstimatePdf(estimate.id, {
          filename: buildAdasReportFilename({ make: estimate.make, ro: estimate.ro, vin: estimate.vin })
        })
          .catch((err) => {
            console.error('PDF export failed', err)
            setDownloadError(err?.message || 'Failed to generate PDF')
          })
          .finally(() => setIsDownloading(false))
      }
    })
    return () => clearPageAction()
  }, [estimate?.id, estimate?.ro, estimate?.vin, isDownloading, setPageAction, clearPageAction])

  if (isDetailLoading && !estimate) {
    return (
      <div className='mx-auto max-w-[800px] py-16 text-center'>
        <Loader2 className='mx-auto mb-4 h-10 w-10 animate-spin text-[#1F6FEB]' />
        <div className='text-lg font-semibold text-[#0A1628]'>Loading estimate...</div>
      </div>
    )
  }

  if (!estimate && detailRefusal?.code === 'RO_LOCKED') {
    return <RoLockedView refusal={detailRefusal} onBack={() => history.push('/adas-intelligence')} />
  }

  if (!estimate) {
    return (
      <div className='mx-auto max-w-[800px] py-12 text-center'>
        <div className='mb-4 text-lg font-semibold text-[#0A1628]'>Estimate not found</div>
        <div className='mb-4 text-sm text-[#6B7280]'>{detailError ? detailError : `Estimate ${id} could not be loaded.`}</div>
        <div className='flex justify-center gap-2'>
          <Button variant='outline' onClick={() => history.goBack()}>
            <ArrowLeft className='mr-2 h-4 w-4' />
            Back to ADAS Intelligence
          </Button>
          {detailError && (
            <Button variant='outline' onClick={() => loadDetail(id)}>
              Retry
            </Button>
          )}
        </div>
      </div>
    )
  }

  const onDownload = async () => {
    if (!estimate?.id) return
    setIsDownloading(true)
    setDownloadError(null)
    try {
      await downloadScrubEstimatePdf(estimate.id, {
        filename: buildAdasReportFilename({ make: estimate.make, ro: estimate.ro, vin: estimate.vin })
      })
    } catch (err) {
      console.error('PDF export failed', err)
      setDownloadError(err?.message || 'Failed to generate PDF')
    } finally {
      setIsDownloading(false)
    }
  }

  const onCopyVin = async () => {
    try {
      await navigator.clipboard.writeText(estimate.vin)
      setCopied(true)
      setTimeout(() => setCopied(false), 1400)
    } catch {
      /* ignore */
    }
  }

  const onCreateQuote = async () => {
    if (!estimate) return
    setIsPreparingQuote(true)
    try {
      const calibrationReportFile = await downloadScrubEstimatePdf(estimate.id, {
        asFile: true,
        filename: buildAdasReportFilename({ make: estimate.make, ro: estimate.ro, vin: estimate.vin })
      })

      // Best-effort: also carry the ORIGINAL uploaded estimate file into the New Quote
      // "Collision Repair Estimate" slot (NewQuote routes estimateReportFile there).
      // Legacy/purged estimates can 404 — never block quote creation over it.
      let estimateReportFile = null
      try {
        estimateReportFile = await downloadOriginalEstimateFile(estimate.id, {
          asFile: true,
          filename: buildEstimateFilename({ make: estimate.make, ro: estimate.ro, vin: estimate.vin })
        })
      } catch (fileErr) {
        console.error('Original estimate file unavailable', fileErr)
      }

      history.push(REDIRECT_URLS.NEW_QUOTE, {
        prepopulateVin: estimate.vin && estimate.vin !== '—' ? estimate.vin : undefined,
        aro: estimate.ro,
        clientName: estimate.client && estimate.client !== '—' ? estimate.client : undefined,
        insuranceName: estimate.insurance && estimate.insurance !== '—' ? estimate.insurance : undefined,
        // Backend-resolved canonical insurer id (CAR-3767). Lets New Quote preselect the dropdown
        // reliably instead of relying on brittle name-string equality against the extracted text.
        insuranceCompanyId: estimate.insuranceCompanyId ?? undefined,
        calibrationReportFile,
        estimateReportFile,
        estimateSource: { id: estimate.id, ro: estimate.ro }
      })
    } catch (err) {
      console.error('Create Quote failed', err)
      setIsPreparingQuote(false)
    }
  }

  const onAddCalibration = async (form) => {
    if (!estimate?.id || !estimate?.ro || !form?.displayTitle) return

    // Restored Safety Ops carry section='Safety Operations' + productType='Service'; everything
    // else (form-driven Adds, ADAS restores) defaults to ADAS Calibrations / Calibration.
    const targetSection = form.section || 'ADAS Calibrations'
    const productType = form.productType || (targetSection === 'Safety Operations' ? 'Service' : 'Calibration')

    // 1) Optimistic insert — the card appears in the matching section immediately.
    const optimistic = {
      n: `pending-${Date.now()}`,
      system: form.displayTitle,
      category: form.adasCategory || '',
      calType: form.calibrationMethod || '',
      confidence: '',
      firedRuleIds: ['MANUAL'],
      triggeredBy: '',
      oemNote: '',
      displayDocument: '',
      location: '',
      make: '',
      section: targetSection
    }
    setPendingAdditions((prev) => (prev.some((p) => p.system === optimistic.system) ? prev : [...prev, optimistic]))
    setCustomSystems((prev) => {
      const next = new Set(prev)
      next.add(form.displayTitle)
      return next
    })
    setShowAddForm(false)

    // 2) Fire the backend call. On success the detail reloads and this card is
    //    picked up from the server data; the cleanup effect drops the optimistic stub.
    //    On failure we roll back and surface the error.
    setIsAdding(true)
    setCalibrationError(null)
    try {
      await addCalibration(estimate.id, estimate.ro, {
        ...form,
        productType,
        calibrationCenterId: ccId,
        performedBy: currentUser?.email || ''
      })
    } catch (err) {
      console.error('Add calibration failed', err)
      setCalibrationError(err?.message || 'Failed to add calibration')
      setPendingAdditions((prev) => prev.filter((p) => p.system !== optimistic.system))
      setCustomSystems((prev) => {
        const next = new Set(prev)
        next.delete(form.displayTitle)
        return next
      })
    } finally {
      setIsAdding(false)
    }
  }

  // Apply the dropdown change optimistically, then PATCH the backend with the human-readable
  // name (the API contract takes insurer_name as a string).
  const onSelectInsurance = async (nextId) => {
    setSelectedInsurance(nextId)
    if (!estimate?.id) return
    const match = insuranceCompanies?.find((c) => String(c.id) === String(nextId))
    const insurerName = match?.name ?? match?.value ?? ''
    setCalibrationError(null)
    try {
      await patchEstimateMeta(estimate.id, ccId, { insurer_name: insurerName })
    } catch (err) {
      console.error('Patch insurer failed', err)
      setCalibrationError(err?.message || 'Failed to update insurance')
    }
  }

  // Apply the client dropdown change optimistically, then PATCH the backend with the
  // human-readable name (the API contract takes client_name as a string).
  const onSelectClient = async (nextId) => {
    setSelectedClient(nextId)
    if (!estimate?.id) return
    const match = clients?.find((c) => String(c.id) === String(nextId))
    const clientName = match?.name ?? match?.value ?? ''
    setCalibrationError(null)
    try {
      await patchEstimateMeta(estimate.id, ccId, { client_name: clientName })
    } catch (err) {
      console.error('Patch client failed', err)
      setCalibrationError(err?.message || 'Failed to update client')
    }
  }

  const onRemoveCalibration = async (finding) => {
    if (!estimate?.id || !estimate?.ro || !finding?.system) return
    setCalibrationError(null)
    try {
      await removeCalibration(estimate.id, estimate.ro, {
        calibrationCenterId: ccId,
        displayTitle: finding.system,
        performedBy: currentUser?.email || ''
      })
    } catch (err) {
      console.error('Remove calibration failed', err)
      setCalibrationError(err?.message || 'Failed to remove calibration')
    }
  }

  // Runs only after the user confirms the dismiss dialog. Mirrors the old inline
  // onDismiss body: record the card in the client-only dismissedBySystem map, drop
  // any "custom" marker, and fire the backend REMOVE.
  const performDismiss = ({ finding, section, isCustom }) => {
    if (!finding?.system) return
    setDismissedBySystem((prev) => {
      const next = new Map(prev)
      next.set(finding.system, {
        system: finding.system,
        section,
        category: finding.category,
        calType: finding.calType,
        dismissedAt: new Date().toISOString(),
        dismissedBy: currentUser?.name || currentUser?.email || 'User',
        reason: 'Removed from calibration list'
      })
      return next
    })
    if (isCustom) {
      setCustomSystems((prev) => {
        const next = new Set(prev)
        next.delete(finding.system)
        return next
      })
    }
    onRemoveCalibration(finding)
  }

  // Restore a dismissed card via the BE RESTORE action, which rehydrates the ORIGINAL
  // finding (OEM reference, triggered-by lines, fired rule IDs, confidence) from the
  // removal audit snapshot — so the card comes back exactly as it was, NOT as a
  // Custom/MANUAL card. `entry` is a dismissed-drawer row; entry.finding carries the full
  // snapshot when we have it (BE removedCalibrations), otherwise we fall back to the row.
  const onRestoreCalibration = async (entry) => {
    if (!estimate?.id || !estimate?.ro || !entry?.system) return
    const original = entry.finding || entry
    const restoreSection = entry.section || 'ADAS Calibrations'

    // Optimistically pull the card out of the dismissed drawer and render it back in its
    // section. It is NOT added to customSystems, so it shows with its real confidence/rule
    // pills rather than the Custom badge. The BE round-trip then replaces the stub with the
    // fully-rehydrated card.
    setRestoredBySystem((prev) => {
      const next = new Set(prev)
      next.add(entry.system)
      return next
    })
    setDismissedBySystem((prev) => {
      const next = new Map(prev)
      next.delete(entry.system)
      return next
    })
    setPendingAdditions((prev) =>
      prev.some((p) => p.system === entry.system)
        ? prev
        : [...prev, { ...original, n: original.n ?? `restore-${entry.system}`, section: restoreSection }]
    )

    setIsAdding(true)
    setCalibrationError(null)
    try {
      await restoreCalibration(estimate.id, estimate.ro, {
        calibrationCenterId: ccId,
        displayTitle: entry.system,
        performedBy: currentUser?.email || '',
        reason: 'Restored'
      })
    } catch (err) {
      console.error('Restore calibration failed', err)
      setCalibrationError(err?.message || 'Failed to restore calibration')
      // Roll back: drop the optimistic card and return the entry to the dismissed drawer.
      setPendingAdditions((prev) => prev.filter((p) => p.system !== entry.system))
      setRestoredBySystem((prev) => {
        const next = new Set(prev)
        next.delete(entry.system)
        return next
      })
      setDismissedBySystem((prev) => {
        const next = new Map(prev)
        next.set(entry.system, entry)
        return next
      })
    } finally {
      setIsAdding(false)
    }
  }

  const onMarkReviewed = async () => {
    if (!estimate?.id) return
    setIsMarking('review')
    try {
      await markReviewed(estimate.id)
    } catch (err) {
      console.error('Mark reviewed failed', err)
    } finally {
      setIsMarking(null)
    }
  }

  const totals = estimate.totals || {}

  // Live KPI counts: derive from what's actually rendered so the tiles react to
  // optimistic add/remove (and dismissals) before the backend round-trip lands.
  // BE totals are only used as fallbacks while findings haven't loaded yet — once
  // findings are present we trust the visible-count, even when it's zero.
  const displayedCounts = (() => {
    let calibrations = 0
    let safety = 0
    const findingsGroups = estimate.findings || []
    findingsGroups.forEach((g) => {
      const visible = g.items.filter((it) => !dismissedBySystem.has(it.system)).length
      if (g.section === 'ADAS Calibrations') calibrations += visible
      else if (g.section === 'Safety Operations') safety += visible
    })
    // Bucket optimistic adds by their target section (Safety restores no longer leak
    // into the Calibrations count).
    pendingAdditions
      .filter((p) => !dismissedBySystem.has(p.system))
      .forEach((p) => {
        const section = p.section || 'ADAS Calibrations'
        if (section === 'Safety Operations') safety += 1
        else calibrations += 1
      })
    const hasFindings = findingsGroups.length > 0 || pendingAdditions.length > 0
    return {
      calibrations: hasFindings ? calibrations : (totals.calibrations ?? 0),
      safety: hasFindings ? safety : (totals.safety ?? 0),
      // Total Findings now mirrors the two visible KPI tiles. Dismissing a card
      // drops both the per-section count and Total Findings by one.
      findings: hasFindings ? calibrations + safety : (totals.findings ?? 0)
    }
  })()

  // Shared right-column cards (Installed Safety Systems + Estimate History). Rendered on
  // both the Findings and Estimate tabs so they stay visible regardless of which tab is active.
  const sidebarCards = (
    <>
      <section className='rounded-xl border border-[#D1D5D8] bg-white p-4'>
        <h3 className='mb-3 text-sm font-semibold text-[#0A1628]'>Installed Safety Systems</h3>
        <ul className='space-y-1.5 text-[13px]'>
          {estimate.installedSystems.map((s) => {
            const isInFindings = estimate.findings.some((g) => g.items.some((it) => it.system === s))
            return (
              <li key={s} className='flex items-start gap-2'>
                <span
                  aria-hidden='true'
                  className='mt-[0.5em] inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full'
                  style={{ backgroundColor: isInFindings ? '#1F6FEB' : '#0A1628' }}
                />
                <span className='text-[#0A1628]'>{s}</span>
              </li>
            )
          })}
        </ul>
      </section>

      <section className='rounded-lg border border-[#D1D5D8] bg-white p-4'>
        <h3 className='mb-3 text-[12px] font-bold uppercase tracking-wide text-[#6B7280]'>Estimate History</h3>
        {versionHistory.length === 0 ? (
          <div className='text-[13px] text-[#6B7280]'>No history available</div>
        ) : (
          (() => {
            // Show the most recent 5 by default; the rest collapse under a Show more
            // toggle so long histories don't dominate the sidebar.
            const visibleVersions = showAllHistory ? versionHistory : versionHistory.slice(0, 5)
            const hiddenCount = versionHistory.length - visibleVersions.length
            return (
              <>
                <ul className='space-y-2 text-[13px]'>
                  {visibleVersions.map((h) => (
                    <li key={h.version} className='flex items-center justify-between'>
                      <div>
                        <div className='text-[#0A1628] font-medium'>Version {h.version}</div>
                        <div className='text-[11px] text-[#6B7280]'>{h.date}</div>
                      </div>
                      {h.latest && (
                        <span className='rounded-full bg-[#D1FAE5] px-2 py-0.5 text-[10px] font-bold uppercase text-[#065F46]'>Latest</span>
                      )}
                    </li>
                  ))}
                </ul>
                {versionHistory.length > 5 && (
                  <button
                    type='button'
                    onClick={() => setShowAllHistory((s) => !s)}
                    className='mt-3 text-[12px] font-medium text-[#1F6FEB] hover:underline'
                  >
                    {showAllHistory ? 'Show less' : `Show ${hiddenCount} more`}
                  </button>
                )}
              </>
            )
          })()
        )}
      </section>
    </>
  )

  return (
    <div className='mx-auto max-w-[1400px] py-4'>
      {/* Mobile vehicle header card — thumb + RO · client + vehicle + VIN */}
      <div className='mb-4 min-[435px]:hidden flex items-stretch gap-3'>
        <div className='flex w-[120px] flex-shrink-0 items-center justify-center overflow-hidden rounded-md bg-[#EBEDF0] text-[#6B7280]'>
          {estimate.vehicleImageUrl ? (
            <img src={estimate.vehicleImageUrl} alt={estimate.vehicle || 'Vehicle'} className='h-full w-full object-cover' />
          ) : (
            <Car className='h-7 w-7' />
          )}
        </div>
        <div className='min-w-0 flex-1 leading-tight'>
          <div className='flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-[#2C3D4D] truncate'>
            RO #{estimate.ro}
            <EstimateLockIcon locked={estimate.estimateLocked} />
          </div>
          {/* Client — always an editable picker; shows the current client when present. */}
          <div className='mt-0.5 flex items-center gap-1 min-w-0'>
            <span className='truncate rounded bg-[#F1F4F8] px-2 py-0.5 text-[11px] font-semibold uppercase tracking-widest text-[#2C3D4D]'>
              {estimate.client && estimate.client !== '—' ? estimate.client : 'Select client'}
            </span>
            {!isReadOnly && (
              <button
                type='button'
                onClick={() => {
                  setClientPickerSearch('')
                  setClientPickerOpen(true)
                }}
                aria-label='Select client'
                className='inline-flex h-4 w-4 items-center justify-center rounded text-[#1F6FEB] hover:bg-[#F1F4F8]'
              >
                <Pencil className='h-3 w-3' />
              </button>
            )}
          </div>
          {/* Insurance + edit pencil */}
          <div className='mt-0.5 flex items-center gap-1 min-w-0'>
            <span className='truncate rounded bg-[#F1F4F8] px-2 py-0.5 text-[11px] font-semibold uppercase tracking-widest text-[#2C3D4D]'>
              {estimate.insurance && estimate.insurance !== '—' ? estimate.insurance : 'Select insurance'}
            </span>
            {!isReadOnly && (
              <button
                type='button'
                onClick={() => {
                  setInsurancePickerSearch('')
                  setInsurancePickerOpen(true)
                }}
                aria-label='Edit insurance company'
                className='inline-flex h-4 w-4 items-center justify-center rounded text-[#1F6FEB] hover:bg-[#F1F4F8]'
              >
                <Pencil className='h-3 w-3' />
              </button>
            )}
          </div>
          <div className='mt-1 text-[16px] font-bold leading-tight text-[#0A1628] truncate'>{estimate.vehicle || 'Estimate'}</div>
          <CollisionBadge collision={estimate.collision} className='mt-1' />
          {estimate.vin && estimate.vin !== '—' && (
            <div className='mt-0.5 font-mono text-[11px] text-[#2C3D4D] truncate'>{estimate.vin}</div>
          )}
        </div>
      </div>

      {/* Back link + page title + actions */}
      <div className='mb-4 hidden min-[435px]:flex flex-wrap items-end justify-between gap-4'>
        <div>
          <button onClick={() => history.goBack()} className='mb-1 text-[13px] font-medium text-[#1F6FEB] hover:underline'>
            Back to ADAS Intelligence
          </button>
          <h1 className='text-[28px] font-bold leading-tight text-[#0A1628]'>{estimate.vehicle || 'Estimate'}</h1>
        </div>
        <div className='flex flex-col items-end gap-1.5'>
          <div className='flex items-center gap-2'>
            {estimate.record?.href ? (
              <button
                onClick={() => history.push(estimate.record.href)}
                className='inline-flex items-center gap-1.5 px-5 py-2.5 bg-gradient-to-r from-[#2C3D4D] to-[#1F2D3D] text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity'
              >
                Linked {estimate.record.stage === 'WORKORDER' ? 'Work Order' : estimate.record.stage === 'INVOICE' ? 'Invoice' : 'Quote'}
                {estimate.record.number ? ` #${estimate.record.number}` : ''}
              </button>
            ) : !canCreateQuote ? null : (
              <button
                onClick={onCreateQuote}
                disabled={isPreparingQuote || isAwaitingAutoQuote}
                className='inline-flex items-center gap-1.5 px-5 py-2.5 bg-gradient-to-r from-[#2C3D4D] to-[#1F2D3D] text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-60 disabled:pointer-events-none'
              >
                {(isPreparingQuote || isAwaitingAutoQuote) && <Loader2 className='h-4 w-4 animate-spin' />}
                {isAwaitingAutoQuote ? 'Generating Quote…' : 'Create Quote'}
              </button>
            )}
            <button
              onClick={onDownload}
              disabled={isDownloading}
              className='inline-flex items-center gap-1.5 px-5 py-2.5 bg-white text-foreground text-sm font-medium rounded-lg border border-border hover:bg-muted transition-colors disabled:opacity-60 disabled:pointer-events-none'
            >
              {isDownloading ? <Loader2 className='h-4 w-4 animate-spin' /> : <Download className='h-4 w-4' />}
              {isDownloading ? 'Preparing...' : 'Download PDF'}
            </button>
            {/* Marking reviewed writes; a client login only reads and downloads (CAR-4055). */}
            {!isReadOnly && estimate.status !== 'reviewed' && estimate.status !== 'quote sent' && (
              <button
                onClick={onMarkReviewed}
                disabled={isMarking === 'review'}
                className='inline-flex items-center gap-1.5 px-5 py-2.5 bg-white text-foreground text-sm font-medium rounded-lg border border-border hover:bg-muted transition-colors disabled:opacity-60 disabled:pointer-events-none'
              >
                {isMarking === 'review' ? <Loader2 className='h-4 w-4 animate-spin' /> : <CheckCircle2 className='h-4 w-4' />}
                {isMarking === 'review' ? 'Marking...' : 'Mark Reviewed'}
              </button>
            )}
            {/* Mark Quote Sent removed — Create Quote flips status to QUOTE_SENT automatically. */}
          </div>
          {/* Auto-quote notices sit under the buttons, so the buttons stay together on one row. */}
          {!estimate.record?.href && estimate.autoQuote?.vehicleInProgress && (
            <span className='max-w-[640px] text-right text-[12px] text-[#B45309]' data-testid='vehicle-in-progress'>
              Not auto-quoted — {estimate.autoQuote.detail || "this vehicle's quote, work order or invoice is still in progress"}.
            </span>
          )}
          {!estimate.record?.href && estimate.autoQuote?.status === 'FAILED' && (
            <span className='max-w-[640px] text-right text-[12px] text-[#B91C1C]' title={estimate.autoQuote.error || ''}>
              Auto-quote failed — create the quote manually.
            </span>
          )}
        </div>
      </div>

      {/* Vehicle summary card */}
      <div className='mb-4 hidden min-[435px]:block rounded-xl border border-[#D1D5D8] bg-white p-4'>
        <div className='flex items-stretch justify-between gap-4'>
          <div className='flex items-start gap-4'>
            <div className='flex h-[100px] w-[160px] flex-shrink-0 items-center justify-center overflow-hidden rounded-md bg-[#EBEDF0] text-[#6B7280]'>
              {estimate.vehicleImageUrl ? (
                <img
                  src={estimate.vehicleImageUrl}
                  alt={estimate.vehicle || 'Vehicle'}
                  className='h-full w-full object-cover'
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                    e.currentTarget.nextElementSibling?.removeAttribute('hidden')
                  }}
                />
              ) : null}
              <Car className='h-10 w-10' hidden={!!estimate.vehicleImageUrl} />
            </div>
            <div className='min-w-0 flex-1'>
              <div className='flex flex-wrap items-center gap-2'>
                <h2 className='text-[18px] font-bold leading-tight text-[#0A1628]'>{estimate.vehicle || '—'}</h2>
                <CollisionBadge collision={estimate.collision} />
              </div>

              {estimate.vin && estimate.vin !== '—' && (
                <div className='mt-3 flex items-center gap-2 font-mono text-[13px] text-[#2C3D4D]'>
                  <span>{estimate.vin}</span>
                  <button onClick={onCopyVin} className='text-[#2C3D4D] hover:text-[#0A1628]' aria-label='Copy VIN'>
                    {copied ? <CheckCircle2 className='h-3.5 w-3.5 text-[#10B981]' /> : <Copy className='h-3.5 w-3.5' />}
                  </button>
                </div>
              )}

              {/* Client + insurance are always editable dropdowns. The client dropdown renders
                  in every case (present or absent) with the current client pre-selected, so the
                  user can review and change it at any time. Insurance is editable the same way. */}
              <div className='mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-2 text-[13px] text-[#6B7280]'>
                <Select value={selectedClient} onValueChange={onSelectClient} disabled={isReadOnly}>
                  <SelectTrigger className='h-7 w-auto min-w-[140px] gap-1 border-[#D1D5D8] bg-transparent px-2 py-0 text-[13px] text-[#2C3D4D] data-[placeholder]:text-[#2C3D4D] focus-visible:ring-[0px]'>
                    <SelectValue placeholder={estimate.client && estimate.client !== '—' ? estimate.client : 'Select client'} />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {c.name || c.value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className='text-[#D1D5D8]'>·</span>
                <Select value={selectedInsurance} onValueChange={onSelectInsurance} disabled={isReadOnly}>
                  <SelectTrigger className='h-7 w-auto min-w-[140px] gap-1 border-[#D1D5D8] bg-transparent px-2 py-0 text-[13px] text-[#2C3D4D] data-[placeholder]:text-[#2C3D4D] focus-visible:ring-[0px]'>
                    <SelectValue placeholder={estimate.insurance && estimate.insurance !== '—' ? estimate.insurance : 'Select insurance'} />
                  </SelectTrigger>
                  <SelectContent>
                    {insuranceCompanies.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {c.name || c.value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          {/* RO # / date pinned to the top-right; OCR indicator pinned to the bottom-right corner. */}
          <div className='flex flex-col items-end justify-between whitespace-nowrap'>
            <div className='flex flex-col items-end gap-0.5'>
              <div className='flex items-center gap-1.5 text-[18px] font-bold leading-tight text-[#2C3D4D]'>
                <EstimateLockIcon locked={estimate.estimateLocked} className='[&>svg]:h-4 [&>svg]:w-4' />
                RO# {estimate.ro}
              </div>
              {estimate.dateReceived && <div className='text-[13px] text-[#6B7280]'>{estimate.dateReceived}</div>}
            </div>
            {estimate.extractionSource === 'LLM' && (
              <div className='mt-3 inline-flex items-center gap-2 rounded-md border border-[#D1D5D8] bg-[#F8FAFC] px-2 py-1'>
                <Scan className='h-4 w-4 flex-none text-[#2C3D4D]' aria-hidden='true' />
                <div className='text-right leading-tight'>
                  <div className='text-[13px] font-bold leading-tight text-[#0A1628]'>OCR Estimate Scan</div>
                  <div className='text-[10px] text-[#6B7280]'>Verify original estimate details</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* KPI row */}
      <div className='mb-6 grid grid-cols-3 gap-4'>
        {[
          { label: 'Calibrations', value: displayedCounts.calibrations },
          { label: 'Safety Operations', value: displayedCounts.safety },
          { label: 'Total Findings', value: displayedCounts.findings }
        ].map((k) => (
          <div
            key={k.label}
            className='flex flex-col items-center rounded-2xl border border-[#D1D5D8] border-t-[3px] border-t-[#1F6FEB] bg-white px-4 py-4 shadow-sm'
          >
            <div className='text-[36px] font-bold leading-none text-[#0F172A]'>{k.value}</div>
            <div className='mt-2 text-[14px] font-medium text-[#6B7280]'>{k.label}</div>
          </div>
        ))}
      </div>

      {detailError && !isDetailLoading && (
        <div className='mb-3 flex items-center gap-2 rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] px-3 py-2 text-sm text-[#B91C1C]'>
          <AlertCircle className='h-4 w-4' />
          {detailError}
          <button onClick={() => loadDetail(id)} className='ml-auto text-[#B91C1C] underline'>
            Retry
          </button>
        </div>
      )}

      {downloadError && (
        <div className='mb-3 flex items-center gap-2 rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] px-3 py-2 text-sm text-[#B91C1C]'>
          <AlertCircle className='h-4 w-4' />
          PDF download failed: {downloadError}
          <button onClick={() => setDownloadError(null)} className='ml-auto text-[#B91C1C] underline'>
            Dismiss
          </button>
        </div>
      )}

      {
        <Tabs defaultValue='findings'>
          {/* Full-width tab bar — divider extends across both columns */}
          <TabsList className='bg-transparent p-0 border-b border-[#D1D5D8] rounded-none w-full justify-start'>
            <TabsTrigger
              value='findings'
              className='rounded-none bg-transparent px-0 pb-2 mr-6 text-[15px] font-semibold text-[#6B7280] data-[state=active]:bg-transparent data-[state=active]:text-[#1F6FEB] data-[state=active]:border-b-2 data-[state=active]:border-[#1F6FEB] data-[state=active]:shadow-none'
            >
              Findings
            </TabsTrigger>
            <TabsTrigger
              value='estimate'
              className='rounded-none bg-transparent px-0 pb-2 text-[15px] font-semibold text-[#6B7280] data-[state=active]:bg-transparent data-[state=active]:text-[#1F6FEB] data-[state=active]:border-b-2 data-[state=active]:border-[#1F6FEB] data-[state=active]:shadow-none'
            >
              Estimate
            </TabsTrigger>
          </TabsList>

          <div className='mt-6'>
            <TabsContent value='findings' className='mt-0'>
              <div className='grid grid-cols-1 gap-4 lg:grid-cols-[1fr_340px]'>
                <div className='space-y-6'>
                  {calibrationError && (
                    <div className='flex items-center gap-2 rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] px-3 py-2 text-sm text-[#B91C1C]'>
                      <AlertCircle className='h-4 w-4' />
                      {calibrationError}
                      <button onClick={() => setCalibrationError(null)} className='ml-auto text-[#B91C1C] underline'>
                        Dismiss
                      </button>
                    </div>
                  )}

                  {(() => {
                    const groups = estimate.findings || []
                    // Always render an ADAS Calibrations section (even when empty) so the
                    // "Add Calibration" CTA has a stable home directly below it. The synthetic
                    // empty group is inserted only when the BE didn't return one.
                    const hasAdas = groups.some((g) => g.section === 'ADAS Calibrations')
                    return hasAdas ? groups : [{ section: 'ADAS Calibrations', items: [] }, ...groups]
                  })().map((group) => {
                    const isAdasSection = group.section === 'ADAS Calibrations'
                    // Optimistic stubs carry their target section (ADAS by default, Safety when
                    // restoring a Safety Op). Items without a section default to ADAS for back-compat.
                    const extras = pendingAdditions.filter(
                      (p) => (p.section || 'ADAS Calibrations') === group.section && !dismissedBySystem.has(p.system)
                    )
                    // Sort manually-added (custom) systems to the bottom of the active stack so a
                    // freshly-added or restored card stays at the bottom on page refresh too —
                    // customSystems is persisted in localStorage.
                    const visibleItems = group.items
                      .filter((it) => !dismissedBySystem.has(it.system))
                      .sort((a, b) => (customSystems.has(a.system) ? 1 : 0) - (customSystems.has(b.system) ? 1 : 0))
                    // ADAS section always renders (so the Add Calibration CTA stays reachable);
                    // Safety / other groups bail out when empty.
                    if (!isAdasSection && visibleItems.length === 0 && extras.length === 0) return null
                    return (
                      <section key={group.section}>
                        <h2 className='mb-3 text-[18px] font-bold text-[#0A1628]'>{group.section}</h2>
                        <div className='space-y-3'>
                          {visibleItems.map((item) => {
                            const isCustom = customSystems.has(item.system)
                            const isSafetySection = group.section === 'Safety Operations'
                            // Ensure a MANUAL rule-id capsule is present on custom cards
                            const enriched = isCustom
                              ? {
                                  ...item,
                                  firedRuleIds: Array.from(new Set([...(item.firedRuleIds || []), 'MANUAL']))
                                }
                              : item
                            return (
                              <FindingCard
                                key={item.n}
                                finding={enriched}
                                custom={isCustom}
                                onDismiss={
                                  isSafetySection || isReadOnly
                                    ? undefined
                                    : (f) => setPendingDismiss({ finding: f, section: group.section, isCustom })
                                }
                              />
                            )
                          })}
                          {/* Optimistic / pending additions render at the bottom so newly added
                            cards land beneath the existing stack, just above the Add CTA. */}
                          {extras.map((item) => (
                            <FindingCard
                              key={item.n}
                              finding={item}
                              // Manual adds live in customSystems (→ Custom badge); restored cards
                              // do not, so they render with their original confidence/rule pills.
                              custom={customSystems.has(item.system)}
                              onDismiss={
                                isReadOnly
                                  ? undefined
                                  : (f) => {
                                      setPendingAdditions((prev) => prev.filter((p) => p.system !== f.system))
                                      setCustomSystems((prev) => {
                                        const next = new Set(prev)
                                        next.delete(f.system)
                                        return next
                                      })
                                    }
                              }
                            />
                          ))}
                        </div>
                        {isAdasSection && (
                          <div className='mt-3'>
                            {!showAddForm && !isReadOnly && (
                              <button
                                type='button'
                                onClick={() => setShowAddForm(true)}
                                className='inline-flex items-center gap-1 text-[13px] font-medium text-[#1F6FEB] hover:opacity-80'
                              >
                                <Plus className='h-4 w-4' />
                                Add Calibration
                              </button>
                            )}
                            {showAddForm && (
                              <AddCalibrationForm
                                onCancel={() => setShowAddForm(false)}
                                onSubmit={onAddCalibration}
                                isSubmitting={isAdding}
                              />
                            )}
                          </div>
                        )}
                      </section>
                    )
                  })}

                  {(() => {
                    // Merge backend-deleted cards (deleted=true on the DTO) with the session-only
                    // dismissedBySystem Map. A system that's currently in the active findings is
                    // hidden from the drawer — covers the Restore case where ADD re-creates the
                    // same calibration but the prior deleted record still lingers in the DTO.
                    // We exclude session-dismissed systems from "active" so the drawer reflects
                    // the optimistic delete immediately, before the backend round-trip lands.
                    const activeSystems = new Set()
                    ;(estimate.findings || []).forEach((g) =>
                      g.items.forEach((it) => {
                        if (it.system && !dismissedBySystem.has(it.system)) activeSystems.add(it.system)
                      })
                    )

                    const merged = new Map()
                    ;(estimate.removedCalibrations || []).forEach((item) => {
                      if (!item?.system || activeSystems.has(item.system)) return
                      // Hide BE-history entries the user just restored locally — keeps the
                      // dismissed drawer in sync until estimate.findings catches up.
                      if (restoredBySystem.has(item.system)) return
                      merged.set(item.system, {
                        system: item.system,
                        section: item.deleted ? 'ADAS Calibrations' : '',
                        category: item.category,
                        calType: item.calType,
                        dismissedAt: item.deletedAt,
                        dismissedBy: item.deletedBy,
                        reason: item.deletedReason || 'Removed',
                        // Full original card — lets Restore render it faithfully in the optimistic
                        // window before the BE round-trip lands.
                        finding: item
                      })
                    })
                    Array.from(dismissedBySystem.values()).forEach((entry) => {
                      if (activeSystems.has(entry.system)) return
                      if (restoredBySystem.has(entry.system)) return
                      if (!merged.has(entry.system)) merged.set(entry.system, entry)
                    })

                    if (merged.size === 0) return null

                    return (
                      <section className='pt-2'>
                        <button
                          type='button'
                          onClick={() => setShowDismissed((s) => !s)}
                          className='mb-2 flex items-center gap-2 text-[13px] text-[#6B7280] hover:opacity-70'
                        >
                          <ChevronDown className={`h-4 w-4 transition-transform ${showDismissed ? '' : '-rotate-90'}`} />
                          {merged.size} dismissed
                        </button>
                        {showDismissed && (
                          <div className='space-y-2'>
                            {Array.from(merged.values()).map((item) => {
                              const dismissedAt = item.dismissedAt ? new Date(item.dismissedAt) : null
                              const dateText =
                                dismissedAt && !Number.isNaN(dismissedAt.getTime())
                                  ? dismissedAt.toLocaleString('en-US', {
                                      month: '2-digit',
                                      day: '2-digit',
                                      year: 'numeric',
                                      hour: 'numeric',
                                      minute: '2-digit',
                                      hour12: true
                                    })
                                  : null
                              const subtitleParts = []
                              if (item.dismissedBy) subtitleParts.push(`Dismissed by ${item.dismissedBy}`)
                              if (dateText) subtitleParts.push(`on ${dateText}`)
                              const subtitle = [subtitleParts.join(' '), item.reason].filter(Boolean).join(' · ')
                              return (
                                <div
                                  key={item.system}
                                  className='relative flex overflow-hidden rounded-lg border border-dashed border-[#D1D5D8] bg-white'
                                >
                                  <div className='w-1.5 flex-shrink-0 bg-[#6B7280]' />
                                  <div className='flex-1 px-4 py-3'>
                                    <div className='mb-1 flex items-center justify-between'>
                                      <div className='text-sm text-[#6B7280] line-through'>{item.system || 'Calibration'}</div>
                                      <button
                                        type='button'
                                        onClick={() => onRestoreCalibration(item)}
                                        className='text-xs font-medium text-[#1F6FEB] hover:underline'
                                      >
                                        Restore
                                      </button>
                                    </div>
                                    <div className='text-[12px] text-[#6B7280]'>{subtitle}</div>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </section>
                    )
                  })()}
                </div>

                {/* Right column — sidebar. Padding-top roughly matches the "ADAS Calibrations"
                    section header so the first sidebar card sits at the same height as the first
                    finding card. */}
                <div className='space-y-4 lg:pt-[42px]'>{sidebarCards}</div>
              </div>
            </TabsContent>

            {/* Estimate tab — a print-style "Estimate" reproducing the CCC Secure
                Share source data for reference, alongside the shared sidebar cards. */}
            <TabsContent value='estimate' className='mt-0'>
              <div className='grid grid-cols-1 gap-2 lg:grid-cols-[minmax(0,1fr)_300px]'>
                <div className='min-w-0 rounded-xl border border-[#D1D5D8] bg-white px-6 py-6 md:px-4 md:py-8'>
                  {/* Report header */}
                  <div className='flex items-center justify-between gap-4 border-b-2 border-[#0A1628] pb-3'>
                    <h2 className='text-[15px] font-bold uppercase tracking-[0.14em] text-[#0A1628] md:text-[17px]'>Estimate</h2>
                    <span className='whitespace-nowrap text-[11px] font-semibold uppercase tracking-[0.18em] text-[#94A3B8] md:text-[12px]'>
                      Reference Only
                    </span>
                  </div>

                  {/* Metadata */}
                  <dl className='mt-5 grid gap-x-4 gap-y-2.5 text-[13px] sm:grid-cols-[160px_1fr] md:grid-cols-[200px_1fr]'>
                    {reportMeta.map((row) => (
                      <Fragment key={row.label}>
                        <dt className='text-[11px] font-semibold uppercase tracking-[0.1em] text-[#94A3B8]'>{row.label}</dt>
                        <dd className='mb-1 text-[#0A1628] sm:mb-0'>{row.value}</dd>
                      </Fragment>
                    ))}
                  </dl>

                  {/* Scrolls rather than compresses below min-w (CAR-3807): the money and hours
                      columns hold unbreakable strings like "1234.56", so squeezing them past ~50px
                      spilled each value into its neighbour. Keep the widths below totalling exactly
                      100% — table-fixed silently normalises any excess, so a colgroup summing to
                      106% renders every column at width/1.06 of what's written here. */}
                  <div className='mt-8 overflow-x-auto'>
                    <table className='w-full min-w-[720px] table-fixed border-collapse text-[13px]'>
                      <colgroup>
                        <col style={{ width: '6%' }} />
                        <col style={{ width: '6%' }} />
                        <col style={{ width: '9%' }} />
                        <col style={{ width: '34%' }} />
                        <col style={{ width: '13%' }} />
                        <col style={{ width: '5%' }} />
                        <col style={{ width: '11%' }} />
                        <col style={{ width: '8%' }} />
                        <col style={{ width: '8%' }} />
                      </colgroup>
                      <thead>
                        <tr className='bg-[#2C3D4D] text-white'>
                          <th className='py-2.5 pl-4 pr-3 text-left text-[11px] font-semibold uppercase tracking-wider'>Line</th>
                          <th className='py-2.5 pr-3 text-left text-[11px] font-semibold uppercase tracking-wider'>Flag</th>
                          <th className='py-2.5 pr-3 text-left text-[11px] font-semibold uppercase tracking-wider'>Oper</th>
                          <th className='py-2.5 pr-3 text-left text-[11px] font-semibold uppercase tracking-wider'>Description</th>
                          <th className='py-2.5 pr-3 text-left text-[11px] font-semibold uppercase tracking-wider'>Part Number</th>
                          <th className='py-2.5 pr-3 text-right text-[11px] font-semibold uppercase tracking-wider'>Qty</th>
                          <th className='py-2.5 pr-3 text-right text-[11px] font-semibold uppercase tracking-wider'>Price</th>
                          <th className='py-2.5 pr-3 text-right text-[11px] font-semibold uppercase tracking-wider'>Labor</th>
                          <th className='py-2.5 pr-4 text-right text-[11px] font-semibold uppercase tracking-wider'>Paint</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(linesBySection).map(([section, lines]) => (
                          <Fragment key={section}>
                            <tr className='bg-[#EEF1F5]'>
                              <td colSpan={9} className='px-4 py-1.5 text-[11px] font-bold uppercase tracking-wide text-[#0A1628]'>
                                {section}
                              </td>
                            </tr>
                            {lines.map((l) => (
                              <tr key={`${section}-${l.line}`} className='border-b border-[#EEF1F5] last:border-b-0'>
                                <td className='py-2 pl-4 pr-3 align-top font-mono text-[12px] text-[#94A3B8]'>{l.line}</td>
                                <td className='py-2 pr-3 align-top'>
                                  {l.triggered && (
                                    <span className='font-mono text-[11px] font-semibold uppercase tracking-wide text-[#5B7A9E]'>S01</span>
                                  )}
                                </td>
                                <td className='py-2 pr-3 align-top font-mono text-[11px] uppercase tracking-wide text-[#8592A3] break-words'>
                                  {l.oper || ''}
                                </td>
                                <td className='py-2 pr-3 align-top text-[#0A1628] break-words'>{l.desc}</td>
                                <td className='py-2 pr-3 align-top font-mono text-[12px] text-[#64748B] break-all'>{l.part || ''}</td>
                                <td className='py-2 pr-3 align-top text-right tabular-nums text-[#0A1628]'>{l.qty || ''}</td>
                                <td className='py-2 pr-3 align-top text-right tabular-nums text-[#0A1628]'>
                                  {l.price ? l.price.toFixed(2) : ''}
                                </td>
                                <td className='py-2 pr-3 align-top text-right tabular-nums text-[#0A1628]'>
                                  {l.labor ? l.labor.toFixed(1) : ''}
                                </td>
                                <td className='py-2 pr-4 align-top text-right tabular-nums text-[#0A1628]'>
                                  {l.paint ? l.paint.toFixed(1) : ''}
                                </td>
                              </tr>
                            ))}
                          </Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Right column — same shared sidebar cards as the Findings tab. */}
                <div className='space-y-4'>{sidebarCards}</div>
              </div>
            </TabsContent>
          </div>
        </Tabs>
      }

      {/* Dismiss confirmation — gates the card X so a finding is never removed by accident */}
      <Dialog open={!!pendingDismiss} onOpenChange={(open) => !open && setPendingDismiss(null)}>
        <DialogContent className='max-w-md'>
          <DialogHeader>
            <DialogTitle>Remove this item?</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove
              {pendingDismiss?.finding?.system ? ` “${pendingDismiss.finding.system}” ` : ' this item '}
              from the calibration list? You can restore it later from the dismissed section.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant='outline' onClick={() => setPendingDismiss(null)}>
              Cancel
            </Button>
            <Button
              variant='destructive'
              onClick={() => {
                const pending = pendingDismiss
                setPendingDismiss(null)
                if (pending) performDismiss(pending)
              }}
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Insurance picker modal */}
      <BrandedModal
        open={insurancePickerOpen}
        onClose={() => setInsurancePickerOpen(false)}
        title='Select insurance company'
        width='w-[420px]'
      >
        <input
          type='text'
          value={insurancePickerSearch}
          onChange={(e) => setInsurancePickerSearch(e.target.value)}
          placeholder='Search insurance companies...'
          className='mb-3 w-full rounded-lg border border-[#D1D5D8] bg-white px-3 py-2 text-[14px] focus:border-[#1F6FEB] focus:outline-none'
        />
        <div className='max-h-[50vh] overflow-y-auto'>
          {insuranceCompanies
            .filter(
              (c) =>
                !insurancePickerSearch.trim() ||
                (c.name || c.value || '').toLowerCase().includes(insurancePickerSearch.trim().toLowerCase())
            )
            .map((c) => {
              const isSelected = String(c.id) === selectedInsurance
              return (
                <button
                  key={c.id}
                  type='button'
                  onClick={() => {
                    onSelectInsurance(String(c.id))
                    setInsurancePickerOpen(false)
                  }}
                  className={`flex w-full items-center justify-between rounded-md px-3 py-2.5 text-left text-[14px] hover:bg-[#F8FAFC] ${
                    isSelected ? 'bg-[#E8EFFF] font-semibold text-[#1F6FEB]' : 'text-[#0A1628]'
                  }`}
                >
                  <span className='truncate'>{c.name || c.value}</span>
                  {isSelected && <CheckCircle2 className='h-4 w-4 text-[#1F6FEB]' />}
                </button>
              )
            })}
          {insuranceCompanies.length === 0 && (
            <div className='py-6 text-center text-[13px] text-[#6B7280]'>No insurance companies available</div>
          )}
        </div>
      </BrandedModal>

      {/* Client picker modal */}
      <BrandedModal open={clientPickerOpen} onClose={() => setClientPickerOpen(false)} title='Select client' width='w-[420px]'>
        <input
          type='text'
          value={clientPickerSearch}
          onChange={(e) => setClientPickerSearch(e.target.value)}
          placeholder='Search clients...'
          className='mb-3 w-full rounded-lg border border-[#D1D5D8] bg-white px-3 py-2 text-[14px] focus:border-[#1F6FEB] focus:outline-none'
        />
        <div className='max-h-[50vh] overflow-y-auto'>
          {clients
            .filter(
              (c) => !clientPickerSearch.trim() || (c.name || c.value || '').toLowerCase().includes(clientPickerSearch.trim().toLowerCase())
            )
            .map((c) => {
              const isSelected = String(c.id) === selectedClient
              return (
                <button
                  key={c.id}
                  type='button'
                  onClick={() => {
                    onSelectClient(String(c.id))
                    setClientPickerOpen(false)
                  }}
                  className={`flex w-full items-center justify-between rounded-md px-3 py-2.5 text-left text-[14px] hover:bg-[#F8FAFC] ${
                    isSelected ? 'bg-[#E8EFFF] font-semibold text-[#1F6FEB]' : 'text-[#0A1628]'
                  }`}
                >
                  <span className='truncate'>{c.name || c.value}</span>
                  {isSelected && <CheckCircle2 className='h-4 w-4 text-[#1F6FEB]' />}
                </button>
              )
            })}
          {clients.length === 0 && <div className='py-6 text-center text-[13px] text-[#6B7280]'>No clients available</div>}
        </div>
      </BrandedModal>

      {/* Mobile floating Mark Reviewed (bottom-left) — only when actionable, and never for a client (CAR-4055) */}
      {!isReadOnly && estimate.status !== 'reviewed' && estimate.status !== 'quote sent' && (
        <button
          type='button'
          onClick={onMarkReviewed}
          disabled={isMarking === 'review'}
          className='min-[435px]:hidden fixed bottom-5 left-5 z-30 inline-flex items-center gap-1.5 rounded-full border border-[#D1D5D8] bg-white px-4 py-3 text-[13px] font-semibold text-[#0A1628] shadow-lg disabled:cursor-not-allowed disabled:opacity-50'
        >
          {isMarking === 'review' ? <Loader2 className='h-4 w-4 animate-spin' /> : <CheckCircle2 className='h-4 w-4' />}
          Mark Reviewed
        </button>
      )}
      {/* Mobile floating Create Quote / View Linked record (bottom-right, like Upload) */}
      {estimate.record?.href ? (
        <button
          type='button'
          onClick={() => history.push(estimate.record.href)}
          className='min-[435px]:hidden fixed bottom-5 right-5 z-30 inline-flex items-center gap-1.5 rounded-full bg-[#0A1628] px-5 py-3 text-[13px] font-semibold text-white shadow-lg'
        >
          View {estimate.record.stage === 'WORKORDER' ? 'Work Order' : estimate.record.stage === 'INVOICE' ? 'Invoice' : 'Quote'}
        </button>
      ) : !canCreateQuote ? null : (
        <button
          type='button'
          onClick={onCreateQuote}
          disabled={isPreparingQuote || isAwaitingAutoQuote}
          className='min-[435px]:hidden fixed bottom-5 right-5 z-30 inline-flex items-center gap-1.5 rounded-full bg-[#0A1628] px-5 py-3 text-[13px] font-semibold text-white shadow-lg disabled:cursor-not-allowed disabled:opacity-50'
        >
          {(isPreparingQuote || isAwaitingAutoQuote) && <Loader2 className='h-4 w-4 animate-spin' />}
          {isAwaitingAutoQuote ? 'Generating Quote…' : 'Create Quote'}
        </button>
      )}
      <div className='min-[435px]:hidden h-[88px]' aria-hidden='true' />
    </div>
  )
}
